import { selectQuotationInsulatedBoxAccessories } from "app/containers/WorkOrder/redux/selector";
import { actions } from "app/containers/WorkOrder/redux/slice";
import { QuotationInsulatedBoxAccessoryCardKey } from "app/containers/WorkOrder/types";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { QuoteSelectOption } from "../../../types";
import {
  FieldsRow,
  StructureCard,
} from "../OpenBoxQuotes/BoxStructureFields";
import { NoteField, NumberField, SelectField } from "../OpenBoxQuotes/FormFields";
import { insulatedKey } from "./BoxStructureCards";

export const useInsulatedBoxAccessories = () =>
  useSelector(selectQuotationInsulatedBoxAccessories);

type CardProps = {
  cardKey: QuotationInsulatedBoxAccessoryCardKey;
  titleKey: string;
};

const resolvedNote = (note: string, t: (key: string) => string) =>
  note.startsWith("QUOTES.") ? t(note) : note;

export const AccessoryNoteCard = ({ cardKey, titleKey }: CardProps) => {
  const dispatch = useDispatch();
  const item = useInsulatedBoxAccessories()[cardKey];

  return (
    <StructureCard
      enabled={item.enabled}
      titleKey={titleKey}
      onToggle={(enabled) =>
        dispatch(actions.updateQuotationInsulatedBoxAccessories({ key: cardKey, value: { enabled } }))
      }
    >
      <NoteField
        labelKey={insulatedKey("FIELDS.NOTE")}
        placeholderKey={insulatedKey("FIELDS.NOTE")}
        value={item.note}
        onChange={(note) =>
          dispatch(actions.updateQuotationInsulatedBoxAccessories({ key: cardKey, value: { note } }))
        }
      />
    </StructureCard>
  );
};

export const AccessoryQuantityCard = ({ cardKey, titleKey }: CardProps) => {
  const dispatch = useDispatch();
  const item = useInsulatedBoxAccessories()[cardKey];

  return (
    <StructureCard
      enabled={item.enabled}
      titleKey={titleKey}
      onToggle={(enabled) =>
        dispatch(actions.updateQuotationInsulatedBoxAccessories({ key: cardKey, value: { enabled } }))
      }
    >
      <NumberField
        labelKey={insulatedKey("FIELDS.QUANTITY")}
        value={item.quantity}
        onChange={(quantity) =>
          dispatch(actions.updateQuotationInsulatedBoxAccessories({ key: cardKey, value: { quantity } }))
        }
      />
    </StructureCard>
  );
};

export const AccessoryTypeCard = ({
  cardKey,
  titleKey,
  options,
}: CardProps & { options: QuoteSelectOption[] }) => {
  const dispatch = useDispatch();
  const item = useInsulatedBoxAccessories()[cardKey];

  return (
    <StructureCard
      enabled={item.enabled}
      titleKey={titleKey}
      onToggle={(enabled) =>
        dispatch(actions.updateQuotationInsulatedBoxAccessories({ key: cardKey, value: { enabled } }))
      }
    >
      <SelectField
        labelKey={insulatedKey("FIELDS.TYPE")}
        value={item.selectedOption}
        options={options}
        onChange={(selectedOption) =>
          dispatch(actions.updateQuotationInsulatedBoxAccessories({ key: cardKey, value: { selectedOption } }))
        }
      />
    </StructureCard>
  );
};

export const AccessoryQuantityNoteCard = ({ cardKey, titleKey }: CardProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const item = useInsulatedBoxAccessories()[cardKey];
  const note = resolvedNote(item.note, t);

  return (
    <StructureCard
      enabled={item.enabled}
      titleKey={titleKey}
      onToggle={(enabled) =>
        dispatch(actions.updateQuotationInsulatedBoxAccessories({ key: cardKey, value: { enabled } }))
      }
    >
      <FieldsRow>
        <NumberField
          labelKey={insulatedKey("FIELDS.QUANTITY")}
          value={item.quantity}
          onChange={(quantity) =>
            dispatch(actions.updateQuotationInsulatedBoxAccessories({ key: cardKey, value: { quantity } }))
          }
          compact
        />
        <NoteField
          labelKey={insulatedKey("FIELDS.NOTE_LOCATION_SIZE")}
          placeholderKey={insulatedKey("FIELDS.NOTE_LOCATION_SIZE")}
          value={note}
          onChange={(nextNote) =>
            dispatch(actions.updateQuotationInsulatedBoxAccessories({ key: cardKey, value: { note: nextNote } }))
          }
          grow
        />
      </FieldsRow>
    </StructureCard>
  );
};

export const AccessoryTypeQuantityNoteCard = ({
  cardKey,
  titleKey,
  options,
}: CardProps & { options: QuoteSelectOption[] }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const item = useInsulatedBoxAccessories()[cardKey];
  const note = resolvedNote(item.note, t);

  return (
    <StructureCard
      enabled={item.enabled}
      titleKey={titleKey}
      onToggle={(enabled) =>
        dispatch(actions.updateQuotationInsulatedBoxAccessories({ key: cardKey, value: { enabled } }))
      }
    >
      <FieldsRow>
        <SelectField
          labelKey={insulatedKey("FIELDS.TYPE")}
          value={item.selectedOption}
          options={options}
          onChange={(selectedOption) =>
            dispatch(actions.updateQuotationInsulatedBoxAccessories({ key: cardKey, value: { selectedOption } }))
          }
          grow
        />
        <NumberField
          labelKey={insulatedKey("FIELDS.QUANTITY")}
          value={item.quantity}
          onChange={(quantity) =>
            dispatch(actions.updateQuotationInsulatedBoxAccessories({ key: cardKey, value: { quantity } }))
          }
          compact
        />
        <NoteField
          labelKey={insulatedKey("FIELDS.NOTE_LOCATION_SIZE")}
          placeholderKey={insulatedKey("FIELDS.NOTE_LOCATION_SIZE")}
          value={note}
          onChange={(nextNote) =>
            dispatch(actions.updateQuotationInsulatedBoxAccessories({ key: cardKey, value: { note: nextNote } }))
          }
          grow
        />
      </FieldsRow>
    </StructureCard>
  );
};
